import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environments';
import { firstValueFrom } from 'rxjs';
import {
  DevelopmentAreaPayload,
  CdpCorridorMapping,
  CdpPayload,
  CdpCorridorFilterOptions,
  CdpCascadingFilterPayload,
  SaveCdpCorridorPayload,
  DeleteCdpCorridorPayload,
} from '../services/interfaces/cdp.corridor-mapping.interfaces';
import { ErrorHandler } from '../utils/error-handler';

@Injectable({
  providedIn: 'root',
})
export class CdpCorridorMappingService {
  private baseUrl = environment.WebApiURL;
  private readonly cdpCorridorExportTabName = 'CDPCorridorSystem';

  private cdpCorridorDropDownUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkCDP/GetCDPCorridorDropDowns`;
  }

  private cdpCorridorMappingUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkCDP/GetCDPCorridorDetails`;
  }

  private saveCDPCorridorUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkCDP/InsertUpdateCDPCorridorSystem`;
  }

  private deleteCdpCorridorUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkCDP/RemoveCDPCorridorSystem`;
  }

  private cdpCascadingFilterUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkCDP/GetCDPCorridorCascadingFilter`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  constructor(private http: HttpClient) {}

  async exportAllCdpCorridorMappings(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.cdpCorridorExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `cdp-corridor-mapping${inferredExtension}`;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return { blob, fileName };
  }

  async getDevelopmentAreas(): Promise<string[]> {
    try {
      const payload: CdpCascadingFilterPayload = {
        developmentArea: null,
        cdpName: null,
      };

      const response = await firstValueFrom(
        this.http.post<any[]>(this.cdpCorridorDropDownUrl(), payload)
      );

      const devAreaItem = response.find((item: any) => item.columnName === 'DevelopmentArea');

      if (!devAreaItem) {
        return [];
      }

      const developmentAreas = devAreaItem.values || [];
      return developmentAreas;
    } catch (error) {
      return [];
    }
  }

  async getFacilityTypes(): Promise<string[]> {
    return ['CDP'];
  }

  async getCdpsByDevArea(devArea: string): Promise<string[]> {
    try {
      const payload: CdpCascadingFilterPayload = {
        developmentArea: devArea,
        cdpName: null,
      };

      const response = await firstValueFrom(
        this.http.post<any[]>(this.cdpCorridorDropDownUrl(), payload)
      );

      const cdpItem = response.find((item: any) => item.columnName === 'CDP');
      return cdpItem?.values || [];
    } catch (error) {
      return [];
    }
  }

  async getCdpCorridorMappings(): Promise<CdpCorridorMapping[]> {
    try {
      const payload: CdpPayload = {
        developmentArea: null,
        facilityTypes: null,
        pseudoCDPNames: null,
        cdpNames: null,
      };

      return this.getCdpCorridorMappingsBySelection(payload);
    } catch (error) {
      return [];
    }
  }

  async getCdpCorridorMappingsBySelection(filters?: Partial<CdpPayload>): Promise<CdpCorridorMapping[]> {
    try {
      const payload: CdpPayload = {
        developmentArea: filters?.developmentArea || null,
        facilityTypes: filters?.facilityTypes || null,
        pseudoCDPNames: filters?.pseudoCDPNames || null,
        cdpNames: filters?.cdpNames || null,
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.cdpCorridorMappingUrl(), payload)
      );

      const cdpCorridorNames = response.cdpCorridorNames || [];

      return cdpCorridorNames.map((item: any) => ({
        developmentArea: item.developmentArea || '',
        facilityType: item.facilityType || 'CDP',
        cdpsToGroup: this.parseCdpsToGroup(item.cdpName || []),
        connectedCdpNetworkName: item.pseudoFacilityName || '',
        isEditing: false,
        isNew: false,
      }));
    } catch (error) {
      return [];
    }
  }

  private parseCdpsToGroup(cdps: any): string[] {
    if (Array.isArray(cdps)) {
      return cdps;
    }
    if (typeof cdps === 'string') {
      return cdps
        .split(',')
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
    }
    return [];
  }

  private parseJsonResponse(response: any): any {
    let parsed = response;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { statusMessage: parsed };
      }
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      parsed = parsed[0];
    }
    return parsed;
  }

  async saveCdpCorridorMapping(
    mapping: CdpCorridorMapping,
    action: 'INSERT' | 'UPDATE',
    userName: string,
    oldConnectedCdpNetworkName?: string
  ): Promise<any> {
    try {
      const body: SaveCdpCorridorPayload = {
        action: action,
        developmentArea: mapping.developmentArea,
        cdpNames: mapping.cdpsToGroup.join(','),
        facilityType: mapping.facilityType,
        pseudoCDPName: mapping.connectedCdpNetworkName,
        oldPseudoCDPName: oldConnectedCdpNetworkName || null,
        userName: userName,
      };

      const response = await firstValueFrom(
        this.http.post(this.saveCDPCorridorUrl(), body, {
          responseType: 'text' as 'json',
        })
      );

      const parsed = this.parseJsonResponse(response);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode,
        statusMessage: result.message,
        error: !result.success,
        ...parsed,
      };
    } catch (error: any) {
      const rawError = error?.error ?? error;
      const parsed = this.parseJsonResponse(rawError);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode || error?.status || 500,
        statusMessage: result.message,
        error: true,
      };
    }
  }

  async deleteCdpCorridorMapping(
    connectedCdpNetworkName: string,
    userName: string
  ): Promise<any> {
    const name = (connectedCdpNetworkName || '').trim();
    const user = (userName || '').trim();

    if (!name) {
      return {
        statusCode: 400,
        statusMessage: 'connectedCdpNetworkName is required.',
        error: true,
      };
    }
    if (!user) {
      return {
        statusCode: 400,
        statusMessage: 'userName is required.',
        error: true,
      };
    }

    const body: DeleteCdpCorridorPayload = {
      pseudoCDPNames: name,
      userName: user,
    };

    try {
      const response = await firstValueFrom(
        this.http.post(this.deleteCdpCorridorUrl(), body, {
          responseType: 'text' as 'json',
        })
      );

      const parsed = this.parseJsonResponse(response);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode,
        statusMessage: result.message,
        error: !result.success,
        ...parsed,
      };
    } catch (error: any) {
      const rawError = error?.error ?? error;
      const parsed = this.parseJsonResponse(rawError);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode || error?.status || 500,
        statusMessage: result.message,
        error: true,
      };
    }
  }

  private parseCascadingFilterResponse(response: any[]): CdpCorridorFilterOptions {
    const getValues = (columnNames: string[]): string[] => {
      const normalizedNames = columnNames.map((name) => name.toLowerCase());
      const item = response.find(
        (entry: any) => normalizedNames.includes((entry?.columnName || '').toLowerCase())
      );
      return item?.values || [];
    };

    return {
      developmentArea: getValues(['DevelopmentArea', 'DevelopmentAreas']),
      facilityType: getValues(['FacilityType', 'FacilityTypes']),
      cdpsToGroup: getValues(['CDP', 'CDPName', 'CDPNames']),
      connectedCdpNetworkName: getValues(['PseudoCDPName', 'PseudoCDPNames']),
    };
  }

  async getFilterOptionsBySelection(filters?: Partial<CdpPayload>): Promise<CdpCorridorFilterOptions> {
    try {
      const payload: DevelopmentAreaPayload = {
        developmentArea: filters?.developmentArea || null,
        facilityTypes: filters?.facilityTypes || null,
        pseudoCDPNames: filters?.pseudoCDPNames || null,
        cdpNames: filters?.cdpNames || null,
      };

      const response = await firstValueFrom(
        this.http.post<any[]>(this.cdpCascadingFilterUrl(), payload)
      );

      return this.parseCascadingFilterResponse(response || []);
    } catch (error) {
      return {
        developmentArea: [],
        facilityType: [],
        cdpsToGroup: [],
        connectedCdpNetworkName: [],
      };
    }
  }

  async getFilterOptions(filterType: 'developmentArea' | 'facilityType' | 'cdpsToGroup' | 'connectedCdpNetworkName', filters?: Partial<CdpPayload>): Promise<string[]> {
    const filterOptions = await this.getFilterOptionsBySelection(filters);
    return filterOptions[filterType] || [];
  }
}

export type { CdpCorridorMapping } from  '../services/interfaces/cdp.corridor-mapping.interfaces';